/**
 * Avatar — displays a user's profile image with initials fallback.
 *
 * Handles the common pattern of showing a rounded image when available,
 * falling back to a colored circle with the user's initials.
 */

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface AvatarProps {
  /** Image URL (renders img if truthy, initials fallback otherwise) */
  src?: string | null;
  /** User's display name (used for alt text and initials extraction) */
  name?: string | null;
  /** Size variant */
  size?: AvatarSize;
  /** Additional CSS classes */
  className?: string;
}

const SIZE_CONFIG: Record<AvatarSize, { container: string; text: string }> = {
  xs: { container: 'h-6 w-6', text: 'text-[10px]' },
  sm: { container: 'h-8 w-8', text: 'text-xs' },
  md: { container: 'h-10 w-10', text: 'text-sm' },
  lg: { container: 'h-12 w-12', text: 'text-base' },
  xl: { container: 'h-16 w-16', text: 'text-xl' },
};

function getInitials(name?: string | null): string {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return parts[0][0]?.toUpperCase() || 'U';
}

export default function Avatar({ src, name, size = 'sm', className = '' }: AvatarProps) {
  const config = SIZE_CONFIG[size];

  if (src) {
    return (
      <img
        src={src}
        alt={name || 'User'}
        className={`${config.container} rounded-full object-cover ring-1 ring-theme-primary ${className}`}
      />
    );
  }

  return (
    <div
      className={`flex ${config.container} items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-300 ${config.text} font-semibold ${className}`}
      title={name || 'User'}
    >
      {getInitials(name)}
    </div>
  );
}
